import ActivityListWidget from '../customWidgets/ucpWidgets/ActivityListWidget';
import KpiWidget from '../customWidgets/ucpWidgets/KpiWidget';

import AiSummaryWidget from './widgets/AiSummaryWidget.jsx';
import AlertsWidget from './widgets/AlertsWidget.jsx';
import BarChartWidget from './widgets/BarChartWidget.jsx';
import BoardWidget from './widgets/BoardWidget.jsx';
import CarouselWidget from './widgets/CarouselWidget.jsx';
import CompositeMetricWidget from './widgets/CompositeMetricWidget.jsx';
import ConnectionsWidget from './widgets/ConnectionsWidget.jsx';
import CorrelationChartWidget from './widgets/CorrelationChartWidget.jsx';
import FunnelChartWidget from './widgets/FunnelChartWidget.jsx';
import GaugeChartWidget from './widgets/GaugeChartWidget.jsx';
import HeatMapWidget from './widgets/HeatMapWidget.jsx';
import LineChartWidget from './widgets/LineChartWidget.jsx';
import ListWidget from './widgets/ListWidget.jsx';
import MapChartWidget from './widgets/MapChartWidget.jsx';
import NextBestActionWidget from './widgets/NextBestActionWidget.jsx';
import PieChartWidget from './widgets/PieChartWidget.jsx';
import ProfileCardWidget from './widgets/ProfileCardWidget.jsx';
import SpendBreakdownWidget from './widgets/SpendBreakdownWidget.jsx';
import StatRowWidget from './widgets/StatRowWidget.jsx';
import SummaryTableWidget from './widgets/SummaryTableWidget.jsx';
import TableWidget from './widgets/TableWidget.jsx';
import UsageMapWidget from './widgets/UsageMapWidget.jsx';

/**
 * Widget type id → the component that draws it.
 *
 * **Why a registry and not a switch.** Four surfaces resolve a widget type —
 * the builder's live preview, the library's detail panel, the library's
 * thumbnail grid, and the dashboard canvas. Each one used to answer "what does
 * type X look like" for itself, which is how the same Donut ended up drawn
 * four different ways. There is one answer here, and every surface reads it.
 *
 * **Why several ids share a component.** Bar and Stacked Bar are one chart in
 * two arrangements, not two charts; the same goes for Line / Area / Sparkline,
 * Pie / Donut / Spend Breakdown, and Heat Map / Usage Heatmap. `props` carries
 * the arrangement, so the drawing stays identical no matter which id you
 * arrive with.
 *
 * **Why three ids point at older components.** `kpi`, `cost_kpi` and `feed`
 * resolve to `KpiWidget` and `ActivityListWidget`, which already shipped and
 * already render exactly this. Re-drawing them here would give the platform
 * two KPI widgets that drift apart.
 *
 * `compactAs` is the prop each component spells "make this narrower" with —
 * the two older ones say `isCompressed`, the newer ones say `compact`, and a
 * caller should not have to know which.
 *
 * @typedef {object} CatalogWidgetEntry
 * @property {import('react').ElementType} component
 * @property {object} [props] Fixed props that select this id's arrangement.
 * @property {'compact'|'isCompressed'|null} [compactAs]
 * @property {{ h: number, minH: number, minW: number }} span Default grid footprint.
 */

/**
 * How tall a type wants to be on a twelve-column grid, in rows.
 *
 * A widget placed on a dashboard has to arrive at *a* height, and letting every
 * surface guess is how the same Table lands at 144px in one place and 384px in
 * another. Three tiers, because there are three shapes here and not
 * twenty-eight: one number, a plot, and a scrolling list of rows.
 *
 * `minH` is the height below which the widget stops being readable rather than
 * merely cramped — a chart with no plot left, a table with no rows.
 *
 * ## Where the numbers come from
 *
 * A card spends about 52px on chrome before the body gets any of it — a 50px
 * header row plus its border — and the body then spends 24px on its own
 * bottom padding. The first pass at these tiers ignored that and sized the
 * rows against the content alone, which left four types scrolling inside a
 * card the author had not asked to scroll: Gauge by 5px, Alerts by 24, AI
 * Summary by 48, Feed by 94.
 *
 * Each tier is now the smallest span its tallest member fits in, plus one row
 * of headroom. Measured, not guessed — the tallest content in each tier is
 * 140px, 272px and 390px, which need 6, 10 and 13 rows.
 */
const SPAN = Object.freeze({
  /** One figure and its context. */
  COMPACT: Object.freeze({ h: 7, minH: 4, minW: 3 }),
  /** A plot, a small list, an agent's read. */
  STANDARD: Object.freeze({ h: 11, minH: 6, minW: 3 }),
  /** Rows the reader scrolls. */
  TALL: Object.freeze({ h: 14, minH: 8, minW: 4 }),
});

const entry = (component, props = null, compactAs = null, span = SPAN.STANDARD) =>
  Object.freeze({ component, props: props ? Object.freeze(props) : null, compactAs, span });

/** @type {Readonly<Record<string, CatalogWidgetEntry>>} */
export const CATALOG_WIDGET_REGISTRY = Object.freeze({
  // ── Statistical ───────────────────────────────────────────────────────────
  kpi: entry(KpiWidget, null, 'isCompressed', SPAN.COMPACT),
  stat_row: entry(StatRowWidget, { variant: 'divided' }, null, SPAN.COMPACT),
  gauge: entry(GaugeChartWidget, null, null, SPAN.COMPACT),
  line_chart: entry(LineChartWidget, { variant: 'line', legendPosition: 'bottom' }),
  bar_chart: entry(BarChartWidget),
  donut: entry(PieChartWidget, { donut: true }),
  funnel: entry(FunnelChartWidget),
  heat_map: entry(HeatMapWidget, { variant: 'matrix' }),
  correlation: entry(CorrelationChartWidget),
  map: entry(MapChartWidget),
  pie: entry(PieChartWidget),
  area: entry(LineChartWidget, { variant: 'area', fillGaps: 'ZERO' }),
  stacked_bar: entry(BarChartWidget, { stacked: true, legendPosition: 'bottom' }),
  sparkline: entry(LineChartWidget, { variant: 'sparkline', fill: true, fillGaps: 'ZERO' }, null, SPAN.COMPACT),
  ai_summary: entry(AiSummaryWidget, null, 'compact'),
  next_best_action: entry(NextBestActionWidget, null, 'compact'),

  // ── Data display ──────────────────────────────────────────────────────────
  // `status` is TEXT in the contract like every other column; the badge is
  // what makes it readable beside the identifier, so the type names it here.
  table: entry(TableWidget, { badgeColumns: ['status'] }, null, SPAN.TALL),
  summary_table: entry(SummaryTableWidget, null, null, SPAN.STANDARD),
  list: entry(ListWidget, null, null, SPAN.TALL),
  profile_card: entry(ProfileCardWidget),
  connections: entry(ConnectionsWidget),
  carousel: entry(CarouselWidget),
  board: entry(BoardWidget, null, null, SPAN.COMPACT),
  feed: entry(ActivityListWidget, null, 'isCompressed', SPAN.TALL),
  alerts: entry(AlertsWidget, null, null, SPAN.COMPACT),

  // ── Consumption ───────────────────────────────────────────────────────────
  // A spend KPI's good direction is down; see `KpiWidget`'s `positiveDirection`.
  cost_kpi: entry(KpiWidget, { positiveDirection: 'DOWN' }, 'isCompressed', SPAN.COMPACT),
  // Cards, not cells: a resource has a name, a percentage and a quota, and
  // a heat map cell has room for none of them.
  usage_heatmap: entry(UsageMapWidget),
  // Bars, not a ring: this type nests, and a sector cannot contain a sector.
  spend_breakdown: entry(SpendBreakdownWidget, null, null, SPAN.STANDARD),
  composite_stat: entry(CompositeMetricWidget, null, null, SPAN.COMPACT),

  // ── Backend renderer vocabulary ───────────────────────────────────────────
  //
  // `POST /v0/dashboard/widget/type/list` publishes 28 presentation types whose
  // `rendererKey` agrees with the ids above for twenty of them. These eight
  // were named differently on the way in; each is the *same drawing*, so they
  // alias rather than duplicate. A widget saved against the catalogue arrives
  // with one of these, and it has to render as what the builder previewed.
  metric_row: entry(StatRowWidget, { variant: 'divided' }, null, SPAN.COMPACT),
  indicator: entry(GaugeChartWidget, null, null, SPAN.COMPACT),
  heatmap: entry(HeatMapWidget, { variant: 'matrix' }),
  pie_chart: entry(PieChartWidget),
  area_chart: entry(LineChartWidget, { variant: 'area', fillGaps: 'ZERO' }),
  activity: entry(ActivityListWidget, null, 'isCompressed', SPAN.TALL),
  usage_map: entry(UsageMapWidget),
  composite_metric: entry(CompositeMetricWidget, null, null, SPAN.COMPACT),

  // ── Catalogue vocabulary ──────────────────────────────────────────────────
  //
  // The Widget Library and the Marketplace filter by a coarser set of names
  // than the builder's twenty-eight types: `chart` covers every chart, because
  // a catalogue you browse does not want twelve chart filters. That coarseness
  // is a product decision and it stays — this alias is what lets a catalogue
  // card render the real widget rather than falling back to a placeholder.
  //
  // It resolves to the line chart, which is the one the platform's own Charts
  // widget draws.
  chart: entry(LineChartWidget, { variant: 'line', legendPosition: 'bottom' }),
});

/** Every type id the registry can draw. */
export const CATALOG_WIDGET_TYPE_IDS = Object.freeze(Object.keys(CATALOG_WIDGET_REGISTRY));

/** Registry entry for a type id, or `null`. */
/**
 * Type ids arrive in two cases: the builder's own lowercase ids and the
 * backend's `typeCode`, which is upper snake (`KPI`, `METRIC_ROW`). They name
 * the same drawing, so the lookup folds the case rather than the registry
 * carrying every key twice.
 */
const normalizeTypeId = (typeId) => String(typeId ?? '').toLowerCase();

export const catalogWidgetEntry = (typeId) =>
  CATALOG_WIDGET_REGISTRY[normalizeTypeId(typeId)] ?? null;

/**
 * How much of a twelve-column grid a type wants, before the author moves it.
 *
 * `w` is not here on purpose: width is what the author chooses (a third, two
 * thirds, the full row), and a default would be overriding the one part of the
 * footprint they have an opinion about. Height is the part nobody wants to
 * pick, so the widget picks it.
 *
 * Falls back to the standard tier for an unknown id, which is what an
 * unimplemented type should occupy while someone notices it is missing.
 */
export const catalogWidgetSpan = (typeId) => catalogWidgetEntry(typeId)?.span ?? SPAN.STANDARD;

/** True when the registry has a real component for this type. */
export const hasCatalogWidget = (typeId) => normalizeTypeId(typeId) in CATALOG_WIDGET_REGISTRY;

export default CATALOG_WIDGET_REGISTRY;
